import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { DiffEntryTitle } from '../diffIndicators/DiffEntryTitle'
import { buildFPlusDiff } from './diffModel'
import { DiffView } from './DiffView'
import { useFormattedFPlusDiffSides } from './useFormattedFPlusDiffSides'

type Props = {
  title: string
  compare: Optional<string>
  current: Optional<string>
}

/**
 * A titled diff between two F-plus expressions. It renders nothing at all when
 * the two sides read the same, because an unchanged row is noise in a list of
 * what changed.
 *
 * Both sides are laid out to one identical width before they are compared, so
 * that the diff reports differences in the expressions rather than differences
 * in how they happen to be laid out. Nothing is shown until they are: a
 * comparison of two sides laid out differently would name lines as rewritten
 * that were only re-wrapped, and then take them back.
 */
export const FPlusDiff = ({ title, compare, current }: Props) => {
  const {
    compare: formattedCompare,
    current: formattedCurrent,
    isResolved,
  } = useFormattedFPlusDiffSides({ compare, current })
  const diff = useMemo(
    () =>
      buildFPlusDiff({ compare: formattedCompare, current: formattedCurrent }),
    [formattedCompare, formattedCurrent],
  )

  if (!isResolved || !diff.isChanged) {
    return null
  }

  return (
    <div>
      <DiffEntryTitle title={title} />
      <DiffView fileDiff={diff.fileDiff} />
    </div>
  )
}
